import type { ManifestObject } from "@shared-core/domain/object-ui/contract";
import { formatValue } from "@shared-core/domain/object-ui/values";
import type { EntityOption } from "../EntityCombobox";

/**
 * One list row as the entity picker offers it: the row's identifier as the label, with its title as the hint. A record
 * people know by a title, not a number, is labelled by its title instead, with the first other column that has a value
 * as the hint (the email that tells two people of one name apart): an object keyed by an opaque uuid (a catalogue item),
 * or a picker sending a column other than the list's key (a customer user, keyed by a link and sent as the person's
 * id). A uuid or a link is the record's address, not something a person reads. `valueField` is the column the field
 * sends: the list's key unless the field says otherwise.
 */
export function optionOf(
  object: ManifestObject,
  item: Record<string, unknown>,
  valueField: string = object.list!.key_field,
): EntityOption {
  const list = object.list!;
  const id = String(item[valueField] ?? "");
  const title = item[list.title_field];
  const isKnownByTitle =
    object.identifier.kind === "uuid" || valueField !== list.key_field;
  if (isKnownByTitle && title != null && title !== "") {
    const other = list.columns.find(
      (c) =>
        c.field !== list.key_field &&
        c.field !== valueField &&
        c.field !== list.title_field &&
        item[c.field] != null &&
        item[c.field] !== "",
    );
    return {
      id,
      label: formatValue(title, "text"),
      ...(other ? { hint: formatValue(item[other.field], other.kind) } : {}),
    };
  }
  return {
    id,
    label: id,
    ...(title != null && list.title_field !== list.key_field
      ? { hint: formatValue(title, "text") }
      : {}),
  };
}
